import React, { useMemo, useState } from "react";
import { OptionsCumulativePnlChart } from "../OptionsAccountPage/OptionsPnlCharts";

function num(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmtAxis(n) {
  if (!Number.isFinite(n)) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? "-" : ""}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${n < 0 ? "-" : ""}${(abs / 1000).toFixed(1)}k`;
  const s = abs >= 100 ? abs.toFixed(0) : abs >= 10 ? abs.toFixed(1) : abs.toFixed(2);
  return `${n < 0 ? "-" : ""}${s}`;
}

function fmtDateLabel(dateStr) {
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  if (parts.length >= 3) return `${parts[1]}-${parts[2]}`;
  return dateStr;
}

const DONUT_COLORS = ["#F0B90B", "#FCD535", "#FFE066", "#848E9C", "#B7BDC6", "#474D57"];

/** At most ~6 x-axis labels; avoids crowding on 30/90-day ranges. */
function shouldShowDailyXLabel(index, total) {
  if (total <= 10) return true;
  const step = Math.max(1, Math.ceil(total / 6));
  return index % step === 0;
}

function ChartTooltip({ point, lines }) {
  if (!point) return null;
  return (
    <div
      className="options-pnl-chart__tooltip"
      style={{ left: point.x, top: point.y }}
    >
      <div className="options-pnl-chart__tooltip-date">{point.date}</div>
      {lines.map((line) => (
        <div key={line.label}>{line.label}: {line.value}</div>
      ))}
    </div>
  );
}

export function SpotDailyPnlChart({ data = [], title = "Daily PNL" }) {
  const [hover, setHover] = useState(null);
  const series = useMemo(
    () => (Array.isArray(data) ? data.map((d) => ({ date: d.date, value: num(d.value) })) : []),
    [data]
  );

  const W = 640;
  const H = 200;
  const pad = { t: 12, r: 16, b: 36, l: 52 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const { minY, maxY, bars, yTicks, zeroY } = useMemo(() => {
    if (!series.length) {
      return { minY: -1, maxY: 1, bars: [], yTicks: [-1, 0, 1], zeroY: pad.t + innerH / 2 };
    }
    const vals = series.map((s) => s.value);
    let min = Math.min(0, ...vals);
    let max = Math.max(0, ...vals);
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const span = max - min || 1;
    const zero = pad.t + ((max - 0) / span) * innerH;
    const step = series.length > 1 ? innerW / series.length : innerW;
    const barW = Math.max(3, Math.min(18, step * 0.6));
    const mapped = series.map((s, i) => {
      const x = pad.l + i * step + (step - barW) / 2;
      const yVal = pad.t + ((max - s.value) / span) * innerH;
      const y0 = zero;
      const top = Math.min(yVal, y0);
      const height = Math.max(2, Math.abs(yVal - y0));
      return { ...s, x, y: top, w: barW, h: height, cx: x + barW / 2 };
    });
    const ticks = [max, max / 2, 0, min / 2, min].filter((v, i, arr) => arr.indexOf(v) === i);
    return { minY: min, maxY: max, bars: mapped, yTicks: ticks, zeroY: zero };
  }, [series, innerH, innerW, pad.l, pad.t]);

  return (
    <div className="options-pnl-chart spot-pnl-daily-chart">
      <div className="options-pnl-chart__title">{title}</div>
      <div className="options-pnl-chart__wrap spot-pnl-daily-chart__wrap">
        {series.length === 0 ? (
          <div className="spot-pnl-chart-empty">No daily PNL data for this range</div>
        ) : (
          <>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid meet"
              className="options-pnl-chart__svg spot-pnl-daily-chart__svg"
              role="img"
              aria-label={title}
            >
              {yTicks.map((tick) => {
                const y = pad.t + ((maxY - tick) / (maxY - minY || 1)) * innerH;
                return (
                  <g key={tick}>
                    <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} className="options-pnl-chart__grid" />
                    <text x={pad.l - 8} y={y + 4} textAnchor="end" className="options-pnl-chart__axis">
                      {fmtAxis(tick)}
                    </text>
                  </g>
                );
              })}
              <line x1={pad.l} y1={zeroY} x2={W - pad.r} y2={zeroY} className="options-pnl-chart__zero" />
              {bars.map((b) => (
                <rect
                  key={b.date}
                  x={b.x}
                  y={b.y}
                  width={b.w}
                  height={b.h}
                  className={b.value >= 0 ? "options-pnl-chart__bar--pos" : "options-pnl-chart__bar--neg"}
                  onMouseEnter={() => setHover({
                    date: b.date,
                    x: `${((b.cx / W) * 100).toFixed(1)}%`,
                    y: 8,
                    value: b.value,
                  })}
                  onMouseLeave={() => setHover(null)}
                />
              ))}
              {bars.map((b, i) => (
                shouldShowDailyXLabel(i, bars.length) ? (
                  <text
                    key={`${b.date}-x`}
                    x={b.cx}
                    y={H - 10}
                    textAnchor="middle"
                    className="options-pnl-chart__axis"
                  >
                    {fmtDateLabel(b.date)}
                  </text>
                ) : null
              ))}
            </svg>
            <ChartTooltip
              point={hover}
              lines={hover ? [{ label: "Daily PNL", value: `${num(hover.value).toFixed(4)} USDT` }] : []}
            />
          </>
        )}
      </div>
    </div>
  );
}

export function SpotCumulativePnlChart(props) {
  return <OptionsCumulativePnlChart {...props} title={props.title || "Cumulative PNL %"} />;
}

export function SpotLineChart({ data = [], title = "Asset Net Worth", valueSuffix = "" }) {
  const series = useMemo(
    () => (Array.isArray(data) ? data.map((d) => ({ date: d.date, value: num(d.value) })) : []),
    [data]
  );

  const W = 520;
  const H = 200;
  const pad = { t: 16, r: 12, b: 28, l: 44 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const { minY, maxY, points, yTicks } = useMemo(() => {
    if (!series.length) {
      return { minY: 0, maxY: 1, points: "", yTicks: [0, 1] };
    }
    const vals = series.map((s) => s.value);
    let min = Math.min(...vals);
    let max = Math.max(...vals);
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const span = max - min || 1;
    const step = series.length > 1 ? innerW / (series.length - 1) : innerW;
    const pts = series.map((s, i) => {
      const x = pad.l + i * step;
      const y = pad.t + ((max - s.value) / span) * innerH;
      return `${x},${y}`;
    }).join(" ");
    return {
      minY: min,
      maxY: max,
      points: pts,
      yTicks: [max, (max + min) / 2, min],
    };
  }, [series, innerH, innerW, pad.l, pad.t]);

  return (
    <div className="options-pnl-chart h-100">
      <div className="options-pnl-chart__title">{title}</div>
      <div className="options-pnl-chart__wrap">
        {series.length === 0 ? (
          <div className="spot-pnl-chart-empty">No chart data for this range</div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="options-pnl-chart__svg" role="img" aria-label={title}>
            {yTicks.map((tick) => {
              const y = pad.t + ((maxY - tick) / (maxY - minY || 1)) * innerH;
              return (
                <g key={tick}>
                  <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} className="options-pnl-chart__grid" />
                  <text x={pad.l - 6} y={y + 4} textAnchor="end" className="options-pnl-chart__axis">
                    {num(tick).toFixed(2)}{valueSuffix}
                  </text>
                </g>
              );
            })}
            {points && (
              <polyline points={points} fill="none" className="options-pnl-chart__line" />
            )}
          </svg>
        )}
      </div>
    </div>
  );
}

export function SpotAssetAllocationChart({ data = [] }) {
  const slices = useMemo(() => {
    const items = (Array.isArray(data) ? data : [])
      .map((d) => ({
        asset: d.asset || "—",
        value: num(d.value_usdt ?? d.pct),
        pct: num(d.pct),
      }))
      .filter((d) => d.value > 0 || d.pct > 0);
    const total = items.reduce((s, d) => s + (d.pct > 0 ? d.pct : d.value), 0);
    if (!total) return [];
    return items.map((item, i) => ({
      ...item,
      share: (item.pct > 0 ? item.pct : (item.value / total) * 100),
      color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));
  }, [data]);

  const arcs = useMemo(() => {
    if (!slices.length) return [];
    let angle = -90;
    const cx = 100;
    const cy = 100;
    const r = 70;
    const ir = 46;
    return slices.map((slice) => {
      const sweep = (slice.share / 100) * 360;
      const start = angle;
      const end = angle + sweep;
      angle = end;
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(start));
      const y1 = cy + r * Math.sin(toRad(start));
      const x2 = cx + r * Math.cos(toRad(end));
      const y2 = cy + r * Math.sin(toRad(end));
      const xi1 = cx + ir * Math.cos(toRad(end));
      const yi1 = cy + ir * Math.sin(toRad(end));
      const xi2 = cx + ir * Math.cos(toRad(start));
      const yi2 = cy + ir * Math.sin(toRad(start));
      const large = sweep > 180 ? 1 : 0;
      const d = `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${ir} ${ir} 0 ${large} 0 ${xi2} ${yi2} Z`;
      return { ...slice, d };
    });
  }, [slices]);

  return (
    <div className="options-pnl-chart h-100">
      <div className="options-pnl-chart__title">Asset Allocation</div>
      <div className="spot-pnl-allocation">
        {slices.length === 0 ? (
          <div className="spot-pnl-chart-empty spot-pnl-chart-empty--center">
            No asset allocation data. Pass include_allocation=1 on the analysis API to load the donut chart.
          </div>
        ) : (
          <>
            <svg viewBox="0 0 200 200" className="spot-pnl-allocation__svg" role="img" aria-label="Asset allocation">
              {arcs.map((arc) => (
                <path key={arc.asset} d={arc.d} fill={arc.color} />
              ))}
            </svg>
            <div className="spot-pnl-allocation__legend">
              {slices.map((slice) => (
                <div key={slice.asset} className="spot-pnl-allocation__legend-item">
                  <i style={{ background: slice.color }} aria-hidden="true" />
                  <span>{slice.asset}</span>
                  <span>{slice.share.toFixed(2)}%</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
